import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { vuser } from '../services/api';
import type { ExperimentPersonaBinding, Persona, PersonaType, VirtualUser, VirtualUserType } from '../types/vuser';
import { isPersonaAlreadyAssigned } from '../utils/experimentPersonaPairs';
import './ExperimentPersonaPairsEditor.css';

export type ExperimentPersonaPairRow = ExperimentPersonaBinding & { key: string };

function newPairKey(): string {
  return `pair-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function bindingsToRows(bindings: ExperimentPersonaBinding[]): ExperimentPersonaPairRow[] {
  return bindings.map((b) => ({ ...b, key: newPairKey() }));
}

export function rowsToBindings(rows: ExperimentPersonaPairRow[]): ExperimentPersonaBinding[] {
  return rows.map(({ virtual_user_id, persona_id }) => ({ virtual_user_id, persona_id }));
}

interface ExperimentPersonaPairsEditorProps {
  title: string;
  hint?: string;
  required?: boolean;
  virtualUserType: VirtualUserType;
  personaType: PersonaType;
  pairs: ExperimentPersonaPairRow[];
  onChange: (pairs: ExperimentPersonaPairRow[]) => void;
  virtualUsers: VirtualUser[];
  integrationId: string | null;
  disabled?: boolean;
}

const ExperimentPersonaPairsEditor: React.FC<ExperimentPersonaPairsEditorProps> = ({
  title,
  hint,
  required = false,
  virtualUserType,
  personaType,
  pairs,
  onChange,
  virtualUsers,
  integrationId,
  disabled = false,
}) => {
  const [draftVirtualUserId, setDraftVirtualUserId] = useState('');
  const [draftPersonaId, setDraftPersonaId] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [personasByVirtualUser, setPersonasByVirtualUser] = useState<Record<string, Persona[]>>({});
  const [loadingPersonas, setLoadingPersonas] = useState(false);
  const loadedPersonaUsersRef = useRef(new Set<string>());
  const loadingPersonaUsersRef = useRef(new Set<string>());

  const eligibleVirtualUsers = useMemo(
    () => virtualUsers.filter((u) => u.type === virtualUserType),
    [virtualUsers, virtualUserType]
  );

  const loadPersonas = useCallback(
    async (virtualUserId: string) => {
      if (!integrationId || !virtualUserId) return;
      if (
        loadedPersonaUsersRef.current.has(virtualUserId) ||
        loadingPersonaUsersRef.current.has(virtualUserId)
      ) {
        return;
      }
      loadingPersonaUsersRef.current.add(virtualUserId);
      setLoadingPersonas(true);
      try {
        const resp = await vuser.listPersonas(integrationId, virtualUserId);
        const list = (resp.data || []).filter((p) => p.type === personaType);
        setPersonasByVirtualUser((prev) => ({ ...prev, [virtualUserId]: list }));
        loadedPersonaUsersRef.current.add(virtualUserId);
      } catch {
        setPersonasByVirtualUser((prev) => ({ ...prev, [virtualUserId]: [] }));
        loadedPersonaUsersRef.current.add(virtualUserId);
      } finally {
        loadingPersonaUsersRef.current.delete(virtualUserId);
        setLoadingPersonas(false);
      }
    },
    [integrationId, personaType]
  );

  useEffect(() => {
    if (!draftVirtualUserId) {
      setDraftPersonaId('');
      return;
    }
    void loadPersonas(draftVirtualUserId);
  }, [draftVirtualUserId, loadPersonas]);

  useEffect(() => {
    pairs.forEach((pair) => {
      if (pair.virtual_user_id) void loadPersonas(pair.virtual_user_id);
    });
  }, [pairs, loadPersonas]);

  const draftPersonas = draftVirtualUserId ? personasByVirtualUser[draftVirtualUserId] ?? [] : [];

  const assignedPersonaIdsForDraftUser = useMemo(() => {
    if (!draftVirtualUserId) return new Set<string>();
    return new Set(
      pairs.filter((p) => p.virtual_user_id === draftVirtualUserId).map((p) => p.persona_id)
    );
  }, [pairs, draftVirtualUserId]);

  const availableDraftPersonas = useMemo(
    () => draftPersonas.filter((p) => !assignedPersonaIdsForDraftUser.has(p.id)),
    [draftPersonas, assignedPersonaIdsForDraftUser]
  );

  useEffect(() => {
    setAddError(null);
  }, [draftVirtualUserId, draftPersonaId]);

  useEffect(() => {
    if (draftPersonaId && assignedPersonaIdsForDraftUser.has(draftPersonaId)) {
      setDraftPersonaId('');
    }
  }, [draftPersonaId, assignedPersonaIdsForDraftUser]);

  const handleAddPair = () => {
    if (!draftVirtualUserId || !draftPersonaId) return;
    if (isPersonaAlreadyAssigned(pairs, draftVirtualUserId, draftPersonaId)) {
      setAddError('This persona is already assigned to the selected virtual user.');
      return;
    }
    setAddError(null);
    onChange([
      ...pairs,
      {
        key: newPairKey(),
        virtual_user_id: draftVirtualUserId,
        persona_id: draftPersonaId,
      },
    ]);
    setDraftVirtualUserId('');
    setDraftPersonaId('');
  };

  const handleRemovePair = (key: string) => {
    onChange(pairs.filter((p) => p.key !== key));
  };

  const resolveVirtualUserName = (id: string) =>
    eligibleVirtualUsers.find((u) => u.id === id)?.name ?? id.substring(0, 8);

  const resolvePersonaName = (virtualUserId: string, personaId: string) =>
    (personasByVirtualUser[virtualUserId] ?? []).find((p) => p.id === personaId)?.name ??
    personaId.substring(0, 8);

  return (
    <div className="exp-persona-pairs">
      <div className="exp-persona-pairs__head">
        <label className="exp-persona-pairs__title">
          {title}
          {required ? ' (required)' : ' (optional)'}
        </label>
        {hint ? <p className="exp-persona-pairs__hint">{hint}</p> : null}
      </div>

      {eligibleVirtualUsers.length === 0 ? (
        <p className="exp-persona-pairs__empty">
          No virtual {virtualUserType === 'admin' ? 'admins' : 'users'} available.
        </p>
      ) : (
        <>
          <div className="exp-persona-pairs__add-row">
            <select
              value={draftVirtualUserId}
              disabled={disabled}
              onChange={(e) => {
                setDraftVirtualUserId(e.target.value);
                setDraftPersonaId('');
                setAddError(null);
              }}
            >
              <option value="">Select virtual {virtualUserType}…</option>
              {eligibleVirtualUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
            <select
              value={draftPersonaId}
              disabled={disabled || !draftVirtualUserId || loadingPersonas}
              onChange={(e) => {
                setDraftPersonaId(e.target.value);
                setAddError(null);
              }}
            >
              <option value="">
                {loadingPersonas
                  ? 'Loading personas…'
                  : availableDraftPersonas.length === 0 && draftVirtualUserId
                    ? 'All personas already assigned'
                    : 'Select persona…'}
              </option>
              {availableDraftPersonas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="exp-persona-pairs__add-btn"
              disabled={
                disabled ||
                !draftVirtualUserId ||
                !draftPersonaId ||
                availableDraftPersonas.length === 0
              }
              onClick={handleAddPair}
            >
              + Add pair
            </button>
          </div>
          {addError ? <p className="exp-persona-pairs__error">{addError}</p> : null}

          {pairs.length > 0 ? (
            <ul className="exp-persona-pairs__list">
              {pairs.map((pair) => (
                <li key={pair.key} className="exp-persona-pairs__item">
                  <span className="exp-persona-pairs__item-text">
                    <strong>{resolveVirtualUserName(pair.virtual_user_id)}</strong>
                    <span className="exp-persona-pairs__sep">→</span>
                    {resolvePersonaName(pair.virtual_user_id, pair.persona_id)}
                  </span>
                  <button
                    type="button"
                    className="exp-persona-pairs__remove"
                    disabled={disabled}
                    onClick={() => handleRemovePair(pair.key)}
                    aria-label="Remove pair"
                    title="Remove"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="exp-persona-pairs__empty">No pairs added yet.</p>
          )}
        </>
      )}
    </div>
  );
};

export default ExperimentPersonaPairsEditor;
